import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fabric, Pivot, PivotItem, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { KundeSearch } from 'components/Kunde/KundeSearch';
import { KundeOverview } from 'components/Kunde/KundeOverview';
import { TerminView } from 'views/TerminView/TerminView';
import { KundeDetail } from 'components/Kunde/KundeDetail';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { AnsprechpartnerView } from 'views/KundeView/AnsprechpartnerView/AnsprechpartnerView';
import { AngebotView } from 'views/AngebotView/AngebotView';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { BestellungView } from 'views/BestellungView/BestellungView';
import { AuftragView } from 'views/AuftragView/AuftragView';
import { ZusatzadresseView } from 'views/KundeView/ZusatzadresseView/ZusatzadresseView';
import { LieferungView } from 'views/LieferungView/LieferungView';
import { RechnungView } from 'views/RechnungView/RechnungView';
import { Helmet } from 'react-helmet';
import { HistoryView } from 'views/KundeView/HistoryView/HistoryView';
import { MaschinenkundeView } from 'views/KundeView/MaschinenkundeView/MaschinenkundeView';
import classnames from 'classnames';
import { SearchIcon } from '@fluentui/react-icons';
import { ServiceView } from '../ServiceView/ServiceView';

const styles = mergeStyleSets({
    container: {
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gridColumnGap: 10,
        overflow: 'hidden',
    },
    containerWithoutSearch: {
        gridTemplateColumns: '1fr',
    },
    searchIcon: {
        fontSize: 20,
        top: -22,
        position: 'relative',
        cursor: 'pointer',
    },
    searchContainer: {
        height: 'calc(100vh - 40px)',
    },
    pivotItem: {
        height: 'calc(100vh - 75px)',
    },
    itemWrapper: {
        height: 'calc(100vh - 75px)',
    },
    subItemWrapper: {
        height: 'calc(100vh - 120px)',
        overflowY: 'scroll',
    },
    newKunde: {
        position: 'absolute',
        right: 15,
        top: 15,
        zIndex: 10000,
    },
});

const pivotStyles = {
    itemContainer: {
        height: 'calc(100vh - 120px)',
    },
};

interface KundeViewParams {
    urlKundeNr?: string;
    page?: string;
    subPage?: string;
}

export const KundeView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const history = useHistory();
    const { urlKundeNr, page, subPage } = useParams<KundeViewParams>();

    const [showSearch, setShowSearch] = React.useState(!urlKundeNr);

    const kundeNrInt = urlKundeNr === 'neu' ? -1 : urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    if (!lookupsLoadingFinished) {
        return null;
    }

    return (
        <Fabric className={classnames(styles.container, { [styles.containerWithoutSearch]: !showSearch })}>
            <Helmet>
                <title>{urlKundeNr ? 'Kunde ' + urlKundeNr : 'Kunden'}</title>
            </Helmet>
            {showSearch && (
                <KundeSearch
                    className={styles.searchContainer}
                    kundeNr={urlKundeNr ? parseInt(urlKundeNr, 10) : null}
                    allowOpenDetails
                />
            )}

            {(auth.rolle === 'ID' || auth.rolle === 'GF') && (
                <PrimaryButton
                    className={styles.newKunde}
                    onClick={() => {
                        history.push('/kunden/neu/details');
                    }}
                    text="Neuer Kunde"
                    disabled={urlKundeNr === 'neu'}
                />
            )}
            {urlKundeNr && (
                <Pivot
                    styles={pivotStyles}
                    selectedKey={page || 'overview'}
                    onLinkClick={(item) => {
                        if (item?.props.itemKey !== 'search') {
                            history.push('/kunden/' + urlKundeNr + '/' + (item?.props.itemKey || ''));
                        }
                    }}
                >
                    <PivotItem
                        itemKey="search"
                        headerButtonProps={{
                            style: { height: 0, width: 28 },
                        }}
                        onRenderItemLink={() => {
                            return (
                                <SearchIcon className={styles.searchIcon} onClick={() => setShowSearch(!showSearch)} />
                            );
                        }}
                        className={styles.itemWrapper}
                    />

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="overview" headerText="Übersicht" className={styles.itemWrapper}>
                            <KundeOverview kundeNr={kundeNrInt} />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && (
                        <PivotItem itemKey="details" headerText="Details" className={styles.itemWrapper}>
                            <Pivot
                                styles={pivotStyles}
                                selectedKey={subPage || 'overview'}
                                onLinkClick={(item) => {
                                    history.push('/kunden/' + urlKundeNr + '/details/' + (item?.props.itemKey || ''));
                                }}
                            >
                                <PivotItem itemKey="details" headerText="Details" className={styles.itemWrapper}>
                                    <KundeDetail kundeNr={kundeNrInt} />
                                </PivotItem>

                                {kundeNrInt !== -1 && (
                                    <PivotItem
                                        itemKey="zusatzadresse"
                                        headerText="Adressen"
                                        className={styles.subItemWrapper}
                                    >
                                        <ZusatzadresseView kNr={kundeNrInt} />
                                    </PivotItem>
                                )}

                                {kundeNrInt !== -1 && (
                                    <PivotItem
                                        itemKey="ansprechpartner"
                                        headerText="Ansprechpartner"
                                        className={styles.subItemWrapper}
                                    >
                                        <AnsprechpartnerView kNr={kundeNrInt} />
                                    </PivotItem>
                                )}

                                {kundeNrInt !== -1 && (
                                    <PivotItem
                                        itemKey="maschinenkunde"
                                        headerText="Maschinen"
                                        className={styles.subItemWrapper}
                                    >
                                        <MaschinenkundeView kNr={kundeNrInt} />
                                    </PivotItem>
                                )}
                            </Pivot>
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="historie" headerText="Historie" className={styles.itemWrapper}>
                            <HistoryView kNr={kundeNrInt} embedded />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="taetigkeiten" headerText="Tätigkeiten" className={styles.itemWrapper}>
                            <TerminView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="angebote" headerText="Angebote" className={styles.itemWrapper}>
                            <AngebotView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="services" headerText="Services" className={styles.itemWrapper}>
                            <ServiceView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="auftraege" headerText="Aufträge" className={styles.itemWrapper}>
                            <AuftragView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="lieferungen" headerText="Lieferungen" className={styles.itemWrapper}>
                            <LieferungView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && (
                        <PivotItem itemKey="rechnungen" headerText="Rechnungen" className={styles.itemWrapper}>
                            <RechnungView />
                        </PivotItem>
                    )}

                    {kundeNrInt !== undefined && kundeNrInt !== -1 && auth.rolle !== 'AD' && (
                        <PivotItem itemKey="bestellungen" headerText="Bestellungen" className={styles.itemWrapper}>
                            <BestellungView />
                        </PivotItem>
                    )}
                </Pivot>
            )}
        </Fabric>
    );
};
